import { useEffect } from 'react';
import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { findScrollContainer, getMountedEditorDom } from './editor-utils';

export const BLOCK_SECTION_SCOPE_CLASS = 'block-section-scope';

export type BlockSectionScopeRange = {
  from: number;
  to: number;
};

export const blockSectionScopePluginKey = new PluginKey<DecorationSet>('blockSectionScope');

function buildScopeDecorations(
  doc: ProseMirrorNode,
  from: number,
  to: number,
): DecorationSet {
  const docSize = doc.content.size;
  const safeFrom = Math.max(0, Math.min(from, docSize));
  const safeTo = Math.max(safeFrom, Math.min(to, docSize));
  if (safeFrom >= safeTo) {
    return DecorationSet.empty;
  }

  const decorations: Decoration[] = [];
  doc.nodesBetween(safeFrom, safeTo, (node, pos) => {
    if (node.isTextblock && pos >= safeFrom && pos < safeTo) {
      decorations.push(
        Decoration.node(pos, pos + node.nodeSize, {
          class: BLOCK_SECTION_SCOPE_CLASS,
        }),
      );
    }
    return true;
  });
  return DecorationSet.create(doc, decorations);
}

export const BlockSectionScope = Extension.create({
  name: 'blockSectionScope',

  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: blockSectionScopePluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, old) {
            const meta = tr.getMeta(blockSectionScopePluginKey) as
              | BlockSectionScopeRange
              | null
              | undefined;
            if (meta === undefined) {
              return tr.docChanged ? old.map(tr.mapping, tr.doc) : old;
            }
            if (meta === null) {
              return DecorationSet.empty;
            }
            return buildScopeDecorations(tr.doc, meta.from, meta.to);
          },
        },
        props: {
          decorations(state) {
            return blockSectionScopePluginKey.getState(state) ?? DecorationSet.empty;
          },
        },
      }),
    ];
  },
});

export function setBlockSectionScopeHighlight(
  editor: Editor,
  range: BlockSectionScopeRange | null,
): void {
  if (editor.isDestroyed) {
    return;
  }
  const editorDom = getMountedEditorDom(editor);
  if (!editorDom) {
    return;
  }
  const scrollContainer = findScrollContainer(editorDom);
  const scrollTop = scrollContainer?.scrollTop;
  const scrollLeft = scrollContainer?.scrollLeft;
  editor.view.dispatch(editor.state.tr.setMeta(blockSectionScopePluginKey, range));
  if (scrollContainer && scrollTop != null && scrollLeft != null) {
    const restore = (): void => {
      scrollContainer.scrollTop = scrollTop;
      scrollContainer.scrollLeft = scrollLeft;
    };
    restore();
    requestAnimationFrame(restore);
  }
}

export function useBlockSectionScopeHighlight(
  editor: Editor | null,
  range: BlockSectionScopeRange | null,
): void {
  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return undefined;
    }
    setBlockSectionScopeHighlight(editor, range);
    return () => {
      if (!editor.isDestroyed) {
        setBlockSectionScopeHighlight(editor, null);
      }
    };
  }, [editor, range?.from, range?.to]);
}
